import { useEffect, useRef } from 'react';
import styles from './DynamicFormBuilder.module.css';

// Thin wrapper around the globally-loaded Ace editor (window.ace, required by
// application/packages_bs5.js). It is intentionally dumb: it seeds once from
// `value` on mount and emits raw text via `onChange` — the parent owns parsing
// and validation. Because the builder only renders this while in JSON mode,
// leaving and re-entering JSON mode unmounts/remounts it, re-seeding from the
// latest definition for free (same re-seed-on-open idea as the preview nonce).
//
// When window.ace is unavailable (e.g. jsdom under vitest) it degrades to a
// plain <textarea> so the surrounding logic stays testable without a stub.
export default function AceJsonEditor({ value, onChange, ariaLabel = 'JSON editor', mode = 'json' }) {
  const hostRef = useRef(null);
  // Keep the latest onChange without re-running the init effect (which would
  // tear down and rebuild the editor, losing cursor/undo state).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!window.ace || !hostRef.current) return undefined;

    const editor = window.ace.edit(hostRef.current);
    const session = editor.getSession();
    session.setMode(`ace/mode/${mode}`);
    editor.setTheme('ace/theme/crimson_editor');
    editor.$blockScrolling = Infinity;
    editor.setShowPrintMargin(false);
    editor.setOptions({ minLines: 20, maxLines: 100, useWorker: true });
    // -1 parks the cursor at the start instead of selecting everything.
    session.setValue(value ?? '', -1);
    editor.on('change', () => onChangeRef.current?.(editor.getValue()));

    // Seed once per mount; later `value` changes flow back from our own onChange.
    return () => editor.destroy();
  }, []);

  if (!window.ace) {
    return (
      <textarea
        ref={hostRef}
        className={`form-control font-monospace small ${styles.aceHost}`}
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  }

  return <div ref={hostRef} className={styles.aceHost} aria-label={ariaLabel} />;
}
